import { Spin } from 'antd';
import ErrorHandler from 'common/error/components/error-handler';
import Spinner from 'common/spinner/components/spinner';
import Sticky from 'common/sticky/sticky';
import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isToggleEnabled } from 'react-awesome-feature-toggles';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import styled from 'styled-components';
import { notification } from 'ui';
import useDeepCompareEffect from 'use-deep-compare-effect';
import moment from 'utils/cops-moment';
import { transformFiltersToUrlParams, transformUrlParamsToFilters } from 'utils/helpers';
import { withRouter } from 'utils/withRouter';
import { v4 as uuidv4 } from 'uuid';
import useSockets from '../../../sockets/components/useSockets';
import {
  subscribeToPlanningBoardEvents,
  unsubscribeToPlanningBoardEvents,
} from '../../../sockets/containers/socket-actions';
import {
  connectionStates,
  fseDefaultAutoSort, fseManualSortColumn, sortTypes,
} from '../../../utils/app-configs';
import {
  isObjectNullOrEmpty,
  sortCOPSModelToParam,
  sortParamToCOPSModel,
} from '../../../utils/helpers';
import useMultiStatusPoll from '../../../utils/useMultiStatusPoll';
import WarningNotification from '../../publish-steps/WarningNotification';
import { laneConfig, viewConfig } from '../config';
import { viewIds } from '../config/view';
import BlockEditSidepanel from '../containers/block-edit-sidepanel-container';
import {
  fetchBoardMetaData,
  resetGanttLaneToFetch,
  setDateReference,
  setLaneData,
  shouldFetchLaneData,
  shouldForceFetchGanttData,
} from '../containers/gantt-actions';
import ganttSockets from '../containers/gantt-sockets';
import useDimensions from '../engine/use-dimensions';
import useGanttEngine from '../engine/use-gantt-engine';
import { getDateToString } from '../utils/date-functions';
import {
  updateFilters as updateWorklistFilters,
} from './../../../worklist/containers/worklist-actions';
import DragLayer from './dnd/containers/dnd-drag-layer-container';
import HeaderCalendar from './header/containers/header-calendar-container';
import HeaderView from './header/containers/header-view-container';
import Lane from './lane/containers/lane-container';
import LaneInfo from './lane/containers/lane-info-container';

const GanttWrapper = styled.div``;

const DataWrapper = styled.div`
  display: grid;
  grid-template-columns: ${laneConfig.widthFirstColumn}px 1fr;
  background-color: #fff;
`;

const DragLayerWrapper = styled.div`
  position: relative;
`;

const CenteredDiv = styled.div`
  padding: 10px;
  text-align: center;
`;

const ColumnInfo = styled.div``;
const ColumnDropzone = styled.div``;

/**
 * Gantt component
 *
 * @param {boardData} object - board Data from api
 * @param {viewData} object - view Data from api
 * @param {view} object - view object
 * @param {setGanttDimensions} function
 * @param {setViewDimensions} function
 * @returns
 */
const Gantt = ({
  boardData,
  viewData,
  view,
  setGanttDimensions,
  setViewDimensions,
  lanesInfo,
  lanesData,
  partiallyExecuted = {},
  setLanesInfo,
  setLanesData,
  fetchBoardData,
  fetchViewData,
  dateReference,
  isLoadingViewData,
  isLoadingBoardData,
  menuVisible,
  error,
  split,
  activeFilters,
  history,
  match,
  updateFilters: updateGanttFilters,
  currentPage,
  setCurrentPage,
  selectedIds,
  planningToPublishMessage,
  fullScreen,
  planningAndViewToOpen,
  setViewInternal,
  activeSort,
  activeView,
  publishAllSuccessMessage,
  abortFailure,
  resetAbort,
  resetBoardData,
  intl,
  resetOpenPlanningAndView,
  activePeriod,
  parent,
  location,
  forceFetchGanttData,
}) => {
  const dispatch = useDispatch();
  const { allFSEsList, defaultSorting, refetchLaneData, ganttLaneToFetch }
    = useSelector(state => state.gantt);
  const [views, setViews] = useState([]);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [filteredLaneData, setFilteredLaneData] = useState();
  const [laneIds, setLaneIds] = useState([]);
  const [lanesInfoPopoverId, setLanesInfoPopoverId] = useState();
  const [manualSortingFromView, setManualSortingFromView] = useState([]);
  const [currentSortDirection, setCurrentSortDirection] = useState([]);
  const [prevViewId, setPrevViewId] = useState();
  const [prevDateReference, setPrevDateReference] = useState();
  const sessionId = useRef();
  const columnInfoRef = useRef(null);
  const ganttContainerRef = useRef(null);
  const ganttRef = useRef(null);
  const [isBlockRendered, setIsBlockRendered] = useState(false);
  const ganttRefCb = useCallback((node) => {
    if (!node && isBlockRendered) {
      setIsBlockRendered(false);
    } else if (!isBlockRendered) {
      setIsBlockRendered(true);
    }
    ganttRef.current = node;
  });
  const { ganttDimensions, viewDimensions, updateView } = useGanttEngine(ganttContainerRef);
  const [ganttRefSize, getDimensions] = useDimensions(ganttRef);
  const subscriptionsRef = useRef([]);
  const { connection, subscriptions } = useSelector(state => state.sockets);
  subscriptionsRef.current = subscriptions;
  const { connectionId } = connection;

  const [connectionState] = useSockets(
    ganttSockets,
    ['useRealtimeEventsForGanttRouteOptimisation', 'useRealtimeEventsForPlanboardGantt'],
  );
  const [multiStatusInfo] = useMultiStatusPoll(
    filteredLaneData,
    null,
    subscriptionsRef?.current?.find(s => s.includes(sessionId?.current)),
  );

  useEffect(() => {
    const dateToShow = dateReference ? getDateToString(dateReference, view) : null;

    if (
      sessionId.current &&
      connectionId &&
      view?.id &&
      dateToShow &&
      connectionState === connectionStates.Connected &&
      isToggleEnabled('useRealtimeEventsForPlanboardGantt') &&
      !subscriptionsRef?.current?.find(s =>
        s === `${view?.id}_${dateToShow}_${connectionId}_${sessionId.current}`,
      )
    ) {
      const previousViewId = prevViewId;
      if (view?.id !== previousViewId) {
        setPrevViewId(view?.id);
      }

      const previousStartDate = !isObjectNullOrEmpty(prevDateReference) ?
        getDateToString(prevDateReference) :
        null;
      let previousStart = null;
      if (dateToShow !== previousStartDate) {
        previousStart = previousStartDate;
        setPrevDateReference(dateReference);
      }

      dispatch(subscribeToPlanningBoardEvents({
        previousViewId,
        previousStart,
        viewId: view?.id,
        start: dateToShow,
        connectionId,
        sessionId: sessionId.current,
      }));
    }
  }, [connectionId, view?.id, dateReference, connectionState, sessionId?.current]);

  // Unsubscribe on unmount
  useEffect(() => {
    if (!sessionId && isToggleEnabled('useRealtimeEventsForPlanboardGantt')) {
      sessionId.current = uuidv4();
    }
    return () => {
      const subscriptionId = subscriptionsRef?.current?.find(s => s.includes(sessionId.current));

      if (connectionId &&
        isToggleEnabled('useRealtimeEventsForPlanboardGantt') &&
        subscriptionId
      ) {
        const [viewId, start, ,] = subscriptionId.split('_');
        dispatch(unsubscribeToPlanningBoardEvents({
          viewId,
          start,
          connectionId,
          sessionId: sessionId.current,
        }));
      }
    };
  }, []);

  // Unsubscribe when connection drops
  useEffect(() => {
    const subscriptionId = subscriptionsRef?.current?.find(s => s.includes(sessionId.current));

    if (
      connectionState === connectionStates.Connected &&
      !sessionId.current &&
      isToggleEnabled('useRealtimeEventsForPlanboardGantt')
    ) {
      sessionId.current = uuidv4();
    } else if (connectionId &&
      (
        connectionState === connectionStates.Closed ||
        connectionState === connectionStates.Error
      ) &&
      isToggleEnabled('useRealtimeEventsForPlanboardGantt') &&
      subscriptionId
    ) {
      const [viewId, start, ,] = subscriptionId.split('_');
      dispatch(unsubscribeToPlanningBoardEvents({
        viewId,
        start,
        connectionId,
        sessionId: sessionId.current,
      }));
      sessionId.current = null;
    }
  }, [connectionState]);

  useEffect(() => {
    if (lanesData && lanesData?.length) {
      let filteredData = lanesData?.reduce((blocks, lane) => {
        if (lane?.blocks?.length) {
          return [...blocks, ...lane.blocks];
        }
        return blocks;
      }, [])?.filter(block =>
        block?.durationInMinutes !== 0 &&
        !block.isPreview &&
        !block?.isDeleted &&
        !block?.showAs !== 'hidden',
      );
      if (filteredData && connectionState === connectionStates.Connected) {
        filteredData = filteredData?.filter(block => !block?.id?.includes('forwardplanning'));
      }
      setFilteredLaneData(filteredData);
    }
  }, [lanesData, connectionState]);

  const redirect = (urlFilters) => {
    const { filters, sort, view: worklistView } = match.params;
    history.replace(
      // eslint-disable-next-line
      `/planning-board/${match.params.worklistId}${filters ? `/filters/${filters}` : ''
      // eslint-disable-next-line max-len
      }${sort ? `/sort/${sort}` : ''}${worklistView ? `/view/${worklistView}` : ''
      // eslint-disable-next-line max-len
      }${urlFilters ? `/ganttFilters/${urlFilters}` : ''}${activeSort ? `/ganttSort/${activeSort || defaultSorting || fseDefaultAutoSort}` : ''}${activeView?.id ? `/ganttView/${activeView.id}` : ''}${location && location.pathname.endsWith('/filterselect') ? '/filterselect' : ''}${location && location.pathname.endsWith('ganttfilterselect') ? '/ganttfilterselect' : ''
      }`,
    );
  };

  const fetchGanttData = (page) => {
    const urlFilters = transformFiltersToUrlParams(activeFilters);
    redirect(urlFilters);
    fetchBoardData({
      boardId: viewConfig.boardIdTypes.service,
      filters: urlFilters,
      page,
      sortedFSEs: manualSortingFromView?.length ? manualSortingFromView : allFSEsList,
      sort: activeSort || defaultSorting || fseDefaultAutoSort,
      isAutoSorted: currentSortDirection !== sortTypes.manual,
      keepRows: refetchLaneData,
    });
    dispatch(shouldFetchLaneData(false));
    // reset sorting from view as it should only take effect at init load
    setManualSortingFromView([]);
  };

  const fetchGanttViewData = (laneSubSet) => {
    const dateToShow = getDateToString(dateReference, view);

    fetchViewData({
      boardId: viewConfig.boardIdTypes.service,
      viewId: view?.id,
      viewDate: dateToShow,
      lanes: laneSubSet || laneIds,
      refetchSubSet: !!laneSubSet?.length,
    });
  };

  useEffect(() => {
    dispatch(fetchBoardMetaData());
  }, []);

  const resetOpenData = useCallback(() => {
    resetOpenPlanningAndView();
  }, [resetOpenPlanningAndView]);

  useEffect(() => {
    if (!planningAndViewToOpen?.id) {
      if (match.params.ganttFilters) {
        updateGanttFilters(transformUrlParamsToFilters(match.params.ganttFilters));
      } else {
        updateGanttFilters({});
      }
    }
    return () => {
      document.removeEventListener('mousedown', resetOpenData);
      resetBoardData();
    };
  }, []);

  useEffect(() => {
    if (abortFailure) {
      notification.info({
        message: intl.formatMessage({ id: 'publishSteps.ABORT_NOT_EXECUTED' }),
        description: <WarningNotification
          onConfirm={() => {
            resetAbort();
            notification.destroy('abortInfo');
          }}
          body={<p>{intl.formatMessage({ id: 'publishSteps.ABORT_NOT_EXECUTED_INFO' })}</p>}
          isLoading={false}
          confirmButtonText={intl.formatMessage({ id: 'common.OK' })}
        />,
        key: 'abortInfo',
        className: 'extended-notification',
        duration: 0,
        placement: 'top',
      });
    }
  }, [abortFailure]);

  useDeepCompareEffect(() => {
    if (Object.keys(partiallyExecuted)?.length) {
      notification.warning({
        message: intl?.formatMessage(
          { id: 'toolbar.SOME_PLANNING_FAILED' },
          { failedCount: partiallyExecuted.failedCount, totalCount: partiallyExecuted.totalCount },
        ),
        placement: 'top',
        description: <WarningNotification
          // filter the worklist on the failed order(s)
          onConfirm={() => {
            dispatch(updateWorklistFilters({ order: partiallyExecuted.failedIds }));
            notification.destroy('partiallyExecutedInfo');
          }}
          body={<p>{intl?.formatMessage({
            id: 'toolbar.SOME_PLANNING_FAILED_MESSAGE',
          })}</p>}
          isLoading={false}
          confirmButtonText={intl?.formatMessage({
            id: 'toolbar.SHOW_FAILED_PLANNING_WORKLIST',
          })}
        />,
        key: 'partiallyExecutedInfo',
        className: 'extended-notification',
        duration: 0,
      });
    }
  }, [partiallyExecuted]);

  useEffect(() => {
    if (publishAllSuccessMessage && Object.keys(publishAllSuccessMessage).length) {
      if (publishAllSuccessMessage.type === 'success') {
        notification.success({
          message: publishAllSuccessMessage.message,
          placement: 'top',
        });
      } else if (publishAllSuccessMessage.type === 'error') {
        notification.error({
          message: publishAllSuccessMessage.message,
          placement: 'top',
        });
      } else {
        notification.warning({
          message: publishAllSuccessMessage.message,
          placement: 'top',
        });
      }
    }
  }, [publishAllSuccessMessage?.message, publishAllSuccessMessage?.type]);

  useEffect(() => {
    const page = planningAndViewToOpen?.id ? planningAndViewToOpen?.page : currentPage;
    if (activeView?.id && (page !== 1 || shouldFetch)) {
      fetchGanttData(page);
      if (shouldFetch) {
        // when sort is man, keep the same page
        setCurrentPage(currentSortDirection === sortTypes.manual ? page : 1);
        setShouldFetch(false);
      }
    }
    setShouldFetch(false);
  }, [currentPage, shouldFetch]);

  useEffect(() => {
    if (currentPage === 1 && !shouldFetch) {
      setShouldFetch(true);
    }
  }, [currentPage]);

  useDeepCompareEffect(() => {
    if (activeView?.id && planningAndViewToOpen?.id) {
      if (
        planningAndViewToOpen.page === 1 ||
        planningAndViewToOpen.page === currentPage
      ) {
        setShouldFetch(true);
      } else {
        setCurrentPage(planningAndViewToOpen.page);
      }
    } else if (activeView?.id) {
      if (currentPage === 1) {
        setShouldFetch(true);
      }
      setCurrentPage(1);
    }
  }, [activeFilters]);

  useEffect(() => {
    // If the view changed, update the filters and sort to the ones of the view
    const sort = sortParamToCOPSModel(activeSort);
    setCurrentSortDirection(sort?.find(s => s.sortColumn === fseManualSortColumn)?.sortDirection);

    if (activeView?.id && !planningAndViewToOpen?.id) {
      setShouldFetch(true);
    }
  }, [activeSort]);

  // if you man sort on a none view and active view is also man sort
  useEffect(() => {
    const viewSort = sortCOPSModelToParam(activeView?.sorting);
    const viewSortManual = activeView?.sorting?.find(a => a.sortDirection === sortTypes.manual);

    if (viewSortManual) {
      setManualSortingFromView(
        activeView?.sorting?.find(a => a.sortDirection === sortTypes.manual)?.manualSort,
      );
    }
    if (viewSort === activeSort && !planningAndViewToOpen?.id) {
      setShouldFetch(true);
    }
  }, [activeView?.id]);

  // use useEffect because useDeepCompareEffect will not work on big lists
  useEffect(() => {
    // whenever you change the order of the fse refresh the laneinfo
    if (refetchLaneData) {
      setShouldFetch(true);
    }
  }, [refetchLaneData]);

  useEffect(() => {
    if (
      activeView?.id &&
      !activeView?.isNone &&
      !activeView?.sorting?.length &&
      !activeView?.filterData
    ) {
      setShouldFetch(true);
    }
  }, [activeView?.id]);

  useEffect(() => {
    if (planningToPublishMessage && Object.keys(planningToPublishMessage).length) {
      if (planningToPublishMessage.type === 'success') {
        notification.success({
          message: planningToPublishMessage.message,
          placement: 'top',
        });
      } else if (planningToPublishMessage.type === 'error') {
        notification.error({
          message: planningToPublishMessage.message,
          placement: 'top',
        });
      } else {
        notification.warning({
          message: planningToPublishMessage.message,
          placement: 'top',
        });
      }
    }
  }, [planningToPublishMessage?.message, planningToPublishMessage?.type]);

  /**
   * Set lanesInfo and available views
   */
  useDeepCompareEffect(() => {
    if (boardData && Object.keys(boardData).length) {
      const { lanes, views: boardViews } = boardData;
      setLanesInfo(lanes?.map(lane => ({ ...lane, totalLines: 1, isDraggingOver: false })));
      // if the views are not loaded yet, load the views (initial render)
      if (!views?.length) {
        setViews(boardViews);
        if (planningAndViewToOpen?.id) {
          const currentMoment = moment(planningAndViewToOpen.startDate);
          const dayOfWeek = currentMoment.day();
          const viewToOpen = boardViews?.find(v => v.id === planningAndViewToOpen.viewId);
          const isWeekend = !viewToOpen?.parameters?.workingDays?.includes(dayOfWeek);
          const viewId = isWeekend &&
            (view?.id === viewIds.oneWorkWeek || view?.id === viewIds.twoWorkWeeks) ?
            viewIds.oneWeek :
            planningAndViewToOpen.viewId;
          setViewInternal(boardViews?.find(v => v.id === viewId));
        }
      }
    }
  }, [boardData]);

  useEffect(() => {
    if (
      activeView?.viewRange !== view?.id &&
      views?.length &&
      !planningAndViewToOpen?.id
    ) {
      setViewInternal(views?.find(v => v.id === (activeView?.viewRange || viewIds.oneWorkWeek)));
    }
  }, [activeView?.viewRange, views?.length]);

  /**
   * Set lanes
   */
  useDeepCompareEffect(() => {
    const { lanes } = viewData;
    if (lanes) {
      if (ganttLaneToFetch?.length) {
        // viewdata will have the init state of the lanes. when updating init a forwardplanning
        // viewdata is not updated, when asking data for one lane, viewdata will
        // be also not updating, resulting in showing in deleting forwardplanning on lanes
        dispatch(setLaneData(lanes.filter(lane => ganttLaneToFetch?.includes(lane.id))));
        dispatch(resetGanttLaneToFetch());
      } else {
        const mappedLanesToInfo = [];
        // map lanes based on lanesInfo
        // eslint-disable-next-line
        lanesInfo?.map(info => {
          const lane = lanes?.find(l => l.id === info.id);
          if (lane) {
            mappedLanesToInfo.push(lane);
          } else {
            mappedLanesToInfo.push({});
          }
        });
        setLanesData(mappedLanesToInfo);
      }
    }
  }, [viewData, view, (lanesInfo || []).length]);

  /**
   * Set ganttDimensions
   */
  useDeepCompareEffect(() => {
    if (ganttDimensions) {
      setGanttDimensions(ganttDimensions);
    }
  }, [ganttDimensions]);

  // we use useEffect instead of useDeepCompareEffect, this because we default on null instead of []
  // in the reducer. defaulting on null is needed for to prevent outputing a not intended 0
  useEffect(() => {
    if (lanesInfo && !lanesInfo?.filter(i => i?.preventRefresh)?.length) {
      setLaneIds(lanesInfo?.map(l => l.id));
    }
  }, [JSON.stringify(lanesInfo)]);

  useDeepCompareEffect(() => {
    if (
      laneIds?.length &&
      view?.id &&
      !isObjectNullOrEmpty(dateReference)
    ) {
      fetchGanttViewData();
    }
  }, [laneIds, view?.id, dateReference]);

  useEffect(() => {
    if (ganttLaneToFetch) {
      fetchGanttViewData(ganttLaneToFetch);
    }
  }, [ganttLaneToFetch]);

  useEffect(() => {
    if (forceFetchGanttData) {
      fetchGanttViewData();
      dispatch(shouldForceFetchGanttData(false));
    }
  }, [forceFetchGanttData]);

  /**
   * Set viewDimensions
   */
  useDeepCompareEffect(() => {
    if (viewDimensions) {
      setViewDimensions(viewDimensions);
    }
  }, [viewDimensions]);

  useEffect(() => {
    if (view?.id) {
      updateView(view);
    }
  }, [view?.id]);

  useEffect(() => {
    const block = ganttRef?.current?.querySelector(`#${CSS.escape(planningAndViewToOpen?.id)}`);
    if (block && isBlockRendered) {
      const columnNodes = [...columnInfoRef?.current?.childNodes];
      const lane = columnNodes?.find(n => n.id === planningAndViewToOpen.laneId);
      const container = parent?.current?.pane1;
      if (container && lane) {
        container.scrollTo({
          // eslint-disable-next-line no-mixed-operators
          top: lane.offsetTop + block.offsetTop - 3,
          behavior: 'instant',
        });
      }
    }
  }, [isBlockRendered]);

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (
      planningAndViewToOpen?.id &&
      activeView?.id &&
      views?.length
    ) {
      const currentMoment = moment(planningAndViewToOpen.startDate);
      const dayOfWeek = currentMoment.day();
      const isWeekend = !view?.parameters?.workingDays?.includes(dayOfWeek);
      const newView = isWeekend &&
        (view?.id === viewIds.oneWorkWeek || view?.id === viewIds.twoWorkWeeks) ?
        views?.find(v => v.id === viewIds.oneWeek) :
        view;
      setViewInternal(
        views?.find(v => v.id === (newView?.id === 'currentView' ? activePeriod?.id : newView?.id)),
      );

      const week = currentMoment.week();
      const month = currentMoment.month();
      const year = currentMoment.year();
      let dateRef = {};
      if (
        newView?.id === viewIds.oneDay ||
        newView?.id === viewIds.twoDays
      ) {
        dateRef = {
          day: currentMoment.date(),
          week,
          month,
          year,
        };
      } else if (newView?.id === viewIds.month) {
        const start = currentMoment.startOf('month');
        dateRef = {
          day: start.date(),
          week: start.week(),
          month: start.month(),
          year: start.year(),
        };
      } else {
        const start = currentMoment.startOf('week');
        dateRef = {
          day: start.date(),
          week: start.week(),
          month: start.month(),
          year: start.year(),
        };
      }

      dispatch(
        setDateReference(dateRef),
      );

      if (planningAndViewToOpen?.plannerGroups?.length) {
        if (isEqual(
          activeFilters,
          {
            plannerGroup: planningAndViewToOpen.plannerGroups,
          })) {
          if (
            planningAndViewToOpen.page === 1 ||
            planningAndViewToOpen.page === currentPage
          ) {
            setShouldFetch(true);
          } else {
            setCurrentPage(planningAndViewToOpen.page);
          }
        } else {
          updateGanttFilters({
            plannerGroup: planningAndViewToOpen.plannerGroups,
          });
        }
      } else if (activeFilters && !Object.keys(activeFilters)?.length) {
        if (
          planningAndViewToOpen.page === 1 ||
          planningAndViewToOpen.page === currentPage
        ) {
          setShouldFetch(true);
        } else {
          setCurrentPage(planningAndViewToOpen.page);
        }
      } else {
        updateGanttFilters({});
      }

      document.addEventListener('mousedown', resetOpenData);
    } else {
      document.removeEventListener('mousedown', resetOpenData);
    }
  }, [planningAndViewToOpen?.id, activeView?.id, views?.length]);

  const extraStyles = `
    ${split
      ? `
        top: 55px !important;
      `
      : ''
    }
    ${!split && fullScreen
      ? `
          top: 0px !important;
        `
      : ''
    }
  `;

  const renderLanes = () => {
    if (!lanesInfo || isLoadingBoardData) {
      return <Spinner />;
    }
    if (!lanesInfo?.length) {
      return <CenteredDiv>{intl.formatMessage({ id: 'planningBoard.NO_RESULTS' })}</CenteredDiv>;
    }
    return <DataWrapper>
      <HeaderCalendar />
      <HeaderView columnInfoRef={columnInfoRef} />
    </DataWrapper>;
  };

  return (
    <GanttWrapper ref={ganttContainerRef}>
      {ganttContainerRef?.current &&
        <>
          <ErrorHandler type='notification' error={error} isFullScreen={true} />
          <Sticky
            menuVisible={menuVisible}
            extraStyles={extraStyles}
            translateYEnd={fullScreen ? '0px' : '-51px'}
            zIndex={80}
          >
            {renderLanes()}
          </Sticky>
          {!isLoadingBoardData && lanesInfo?.length ?
            <Spin indicator={<Spinner />} spinning={isLoadingViewData}>
              <DataWrapper>
                <ColumnInfo ref={columnInfoRef}>
                  {lanesInfo?.map((data, key) =>
                    <LaneInfo
                      key={key}
                      data={data}
                      setIsPopoverOpen={setLanesInfoPopoverId}
                      isOpen={lanesInfoPopoverId === data.id}
                    />,
                  )}
                </ColumnInfo>
                <ColumnDropzone ref={ganttRefCb}>
                  <DragLayerWrapper>
                    <DragLayer
                      left={laneConfig.widthFirstColumn}
                      top={ganttRefSize.y}
                      ganttDimensions={ganttDimensions}
                      viewDimensions={viewDimensions}
                      getDimensions={getDimensions}
                      selectedIds={selectedIds}
                    />
                  </DragLayerWrapper>
                  {!isLoadingViewData && lanesData?.map((data, key) =>
                    <Lane
                      key={key}
                      data={data}
                      selectedIds={selectedIds}
                      multiStatusInfo={multiStatusInfo}
                    />,
                  )}
                </ColumnDropzone>
              </DataWrapper>
            </Spin> :
            null
          }
          <Routes>
            <Route
              path={'edit/:type/:planningId'}
              element={<BlockEditSidepanel blockCurrentStatusses={multiStatusInfo} />}
            />
            <Route
              path={'new/:type/:laneId/:date'}
              element={<BlockEditSidepanel
                blockCurrentStatusses={multiStatusInfo}
                isNew={true}
                view={view}
              />}
            />
          </Routes>
        </>
      }
    </GanttWrapper>
  );
};

Gantt.propTypes = {
  forceFetchGanttData: PropTypes.bool,
  boardData: PropTypes.object,
  viewData: PropTypes.object,
  view: PropTypes.object,
  setGanttDimensions: PropTypes.func,
  setViewDimensions: PropTypes.func,
  lanesInfo: PropTypes.array,
  lanesData: PropTypes.array,
  partiallyExecuted: PropTypes.object,
  setLanesInfo: PropTypes.func,
  setLanesData: PropTypes.func,
  fetchBoardData: PropTypes.func,
  fetchViewData: PropTypes.func,
  dateReference: PropTypes.object,
  menuVisible: PropTypes.bool,
  error: PropTypes.object,
  split: PropTypes.bool,
  activeFilters: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
  updateFilters: PropTypes.func,
  isLoadingViewData: PropTypes.bool,
  currentPage: PropTypes.number,
  setCurrentPage: PropTypes.func,
  selectedIds: PropTypes.array,
  planningToPublishMessage: PropTypes.object,
  fullScreen: PropTypes.bool,
  planningAndViewToOpen: PropTypes.object,
  setViewInternal: PropTypes.func,
  publishAllSuccessMessage: PropTypes.object,
  activeSort: PropTypes.string,
  activeView: PropTypes.object,
  location: PropTypes.object,
  abortFailure: PropTypes.bool,
  resetAbort: PropTypes.func,
  intl: PropTypes.object,
  resetBoardData: PropTypes.func,
  resetOpenPlanningAndView: PropTypes.func,
  isLoadingBoardData: PropTypes.bool,
  activePeriod: PropTypes.object,
  parent: PropTypes.object,
};

export default injectIntl(withRouter(Gantt));
